/**
 * Follow-ups — what "Auto Reminders Sent = 0" means for a recall row (redesign Phase 0, 2026-10-02).
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * A straight port of AdminTable.vue's recallReminderSendHour / recallReminderSendHourLabel /
 * recallReminderSetting / recallReminderPending / recallRemindersOn / recallReminderTooltip, as pure
 * functions: the parsed APPT_REMINDER_SETTINGS blob, the row's DateDue and `now` come in as
 * arguments instead of being read from `this` and moment(). The sentences are VERBATIM — the
 * office reads them, and cypress/node-tests/followups-reminder-hint.test.js pins them against the
 * legacy file's own text.
 *
 * Why it exists (from the legacy comment): a bare 0 answers two different questions — "not YET"
 * and "never". On 2026-08-26 a practice read a mid-morning 0 as a failure; the 9 AM send simply
 * hadn't happened. It reports SCHEDULING, never "delivered" — the row carries no send ledger.
 *
 * `now` is { date: 'YYYY-MM-DD', hour: 0–23 } in the site's zone (legacy: moment() in moment's
 * default zone). A Date is accepted too and read by its local fields.
 */

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})(?:$|[ T])/;

function ymdOf(value) {
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    const p = n => (n < 10 ? `0${n}` : String(n));
    return `${value.getFullYear()}-${p(value.getMonth() + 1)}-${p(value.getDate())}`;
  }
  if (typeof value !== 'string') return null;
  const m = YMD_RE.exec(value);
  if (!m) return null;
  const mm = Number(m[2]);
  const dd = Number(m[3]);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
  return `${m[1]}-${m[2]}-${m[3]}`;
}

function nowParts(now) {
  if (now instanceof Date) return { date: ymdOf(now), hour: now.getHours() };
  const n = now || {};
  return { date: n.date, hour: Number(n.hour) };
}

/**
 * APPT_REMINDER_SETTINGS.PropertyValue → the parsed object, or null. Never throws — legacy read
 * each key through a try/catch, so a malformed blob reads as "every key absent".
 */
export function parseReminderSettings(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    return null;
  }
}

function setting(settings, key) {
  return settings && typeof settings === 'object' ? settings[key] : undefined;
}

/** recallReminderTimeOfDayToSend, coerced as the backend's coerceSendHour does: 8–20, else 8. */
export function reminderSendHour(settings) {
  const n = parseInt(setting(settings, 'recallReminderTimeOfDayToSend'), 10);
  return (Number.isInteger(n) && n >= 8 && n <= 20) ? n : 8;
}

export function reminderSendHourLabel(settings) {
  const h = reminderSendHour(settings);
  return h === 12 ? '12 PM' : (h > 12 ? `${h - 12} PM` : `${h} AM`);
}

/**
 * The office's on/off intent — mirrors appt-reminder-service resolveRecallEnabled(). Once a site's
 * exe leg is retired (recallSendLeg 'BACKEND'), recallReminders is the EXE'S MUTE and
 * recallRemindersEnabled carries the intent; absent/blank falls back to recallReminders.
 */
export function recallRemindersOn(settings) {
  const leg = String(setting(settings, 'recallSendLeg') || 'EXE').trim().toUpperCase();
  const legacy = String(setting(settings, 'recallReminders')).toUpperCase() === 'TRUE';
  if (leg !== 'BACKEND') return legacy;
  const raw = setting(settings, 'recallRemindersEnabled');
  if (raw === undefined || raw === null || String(raw).trim() === '') return legacy;
  return String(raw).trim().toUpperCase() === 'TRUE';
}

/** "Pending" = the due date hasn't passed, so a 0 is expected rather than concerning. */
export function recallReminderPending(dateDue, now) {
  if (!dateDue) return false;
  const due = ymdOf(dateDue);
  if (due === null) return false;
  return due >= nowParts(now).date;
}

function mmddyyyy(ymd) {
  return `${ymd.slice(5, 7)}/${ymd.slice(8, 10)}/${ymd.slice(0, 4)}`;
}

/** The tooltip on a 0 — the legacy sentences, word for word. */
export function recallReminderTooltip(settings, dateDue, now) {
  if (!recallRemindersOn(settings)) {
    return 'No automated reminder — Auto Recall Reminders are turned off for this practice. '
      + 'Turn them on under Appointment Reminder settings.';
  }
  if (!dateDue) return 'No automated recall reminder has been sent to this patient.';

  const due = ymdOf(dateDue);
  const n = nowParts(now);
  const at = reminderSendHourLabel(settings);

  if (due !== null && due > n.date) {
    return `Not due yet — the first automated reminder is scheduled for ${mmddyyyy(due)} at ${at}.`;
  }
  if (due !== null && due === n.date) {
    return n.hour < reminderSendHour(settings)
      ? `Due today — the automated reminder is scheduled to go out at ${at}.`
      : `Due today — the automated reminder goes out at ${at} and may still be processing.`;
  }
  return 'No automated reminder was sent for this due date. Most often the patient has no mobile '
    + 'number, is marked "no text messages" in Open Dental, or has opted out. Reach out manually if needed.';
}

/**
 * Everything the Reminders cell needs for one row. `zero` is legacy's `!Number(AutoRemindersSent)`;
 * the hint (icon tone + tooltip) only applies to a zero.
 */
export function reminderCell(row, settings, now) {
  const r = row || {};
  const zero = !Number(r.AutoRemindersSent);
  const pending = zero ? recallReminderPending(r.DateDue, now) : false;
  return {
    count: r.AutoRemindersSent === null || r.AutoRemindersSent === undefined ? '' : String(r.AutoRemindersSent),
    zero,
    pending,
    warn: zero && !pending,
    tooltip: zero ? recallReminderTooltip(settings, r.DateDue, now) : null,
  };
}
